
import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import {
    Zap, TrendingUp, Eye, ShieldCheck,
    CheckCircle2, Users, Calendar, Globe
} from 'lucide-react';

const WhoWeAre = () => {
    const containerRef = useRef(null);

    // The 7 items from your list, exactly as provided
    const requirements = [
        { title: "faster hiring systems", icon: <Zap />, from: "#3b82f6", to: "#2dd4bf", shadow: "rgba(59, 130, 246, 0.5)" },
        { title: "scalable workforce operations", icon: <TrendingUp />, from: "#8b5cf6", to: "#d946ef", shadow: "rgba(139, 92, 246, 0.5)" },
        { title: "recruitment visibility", icon: <Eye />, from: "#06b6d4", to: "#3b82f6", shadow: "rgba(6, 182, 212, 0.5)" },
        { title: "workforce continuity", icon: <Users />, from: "#10b981", to: "#34d399", shadow: "rgba(16, 185, 129, 0.5)" },
        { title: "payroll accuracy", icon: <ShieldCheck />, from: "#f59e0b", to: "#f97316", shadow: "rgba(245, 158, 11, 0.5)" },
        { title: "compliance governance", icon: <CheckCircle2 />, from: "#ef4444", to: "#f87171", shadow: "rgba(239, 68, 68, 0.5)" },
        { title: "long-term workforce planning", icon: <Calendar />, from: "#6366f1", to: "#a855f7", shadow: "rgba(99, 102, 241, 0.5)" },
    ];

    return (
        <section ref={containerRef} className="py-12 bg-white overflow-hidden relative">
            {/* Ambient Background Glows */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <div className="absolute -top-[10%] -left-[10%] w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[120px]" />
                <div className="absolute -bottom-[10%] -right-[10%] w-[500px] h-[500px] bg-purple-400/10 rounded-full blur-[120px]" />
            </div>

            <div className="container mx-auto px-6 relative z-10">
                <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 items-center">

                    {/* LEFT CONTENT: YOUR EXACT TEXT */}
                    <div className="w-full lg:w-[50%]">
                        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
                            <span className="text-blue-600 font-black tracking-[0.5em] text-[10px] uppercase block mb-4">
                                WHO WE ARE?
                            </span>
                            <h2 className="font-bold text-slate-900 syne leading-tight mb-10">
                                <span className="text-xl block mb-2 lg:whitespace-nowrap">Modern Workforce Consulting</span>
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 text-3xl md:text-5xl block">
                                    Built For Business <br /> Transformation
                                </span>
                            </h2>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="space-y-6"
                        >
                            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                                Merida HR Consulting was established to solve one of the biggest challenges modern businesses face today — <span className="font-bold text-slate-800">building scalable workforce systems capable of supporting rapid organizational growth.</span>
                            </p>

                            <p className="text-slate-500 leading-relaxed border-l-4 border-blue-500 pl-6 italic">
                                As a subsidiary of Merida Tech Minds, founded in 2020, Merida HR Consulting combines workforce consulting expertise, recruitment intelligence, operational HR strategy, and scalable workforce infrastructure to support businesses navigating evolving workforce demands.
                            </p>

                            <div className="pt-6">
                                <p className="text-slate-800 font-bold text-lg mb-2">
                                    Traditional recruitment models are no longer sufficient for modern organizations.
                                </p>
                                <p className="text-blue-600 font-bold uppercase tracking-widest text-xs">
                                    Today’s businesses require:
                                </p>
                            </div>
                        </motion.div>
                    </div>

                    {/* RIGHT SIDE: ORBITING NODES (FIXED OVERFLOW) */}
                    <div className="w-full lg:w-[52%] h-[500px] md:h-[600px] relative flex items-center justify-center">
                        <div className="relative w-full h-full flex items-center justify-center scale-[0.5] sm:scale-[0.65] md:scale-[0.75] lg:scale-[0.8] xl:scale-[0.95]">

                            {/* Central Power Core */}
                            <div className="relative w-32 h-32 md:w-40 md:h-40 z-30">
                                <motion.div
                                    animate={{ boxShadow: ["0 0 20px rgba(59, 130, 246, 0.2)", "0 0 50px rgba(99, 102, 241, 0.4)", "0 0 20px rgba(59, 130, 246, 0.2)"] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                    className="w-full h-full bg-[#01014C] rounded-[2.5rem] flex items-center justify-center border border-white/20 relative overflow-hidden p-4"
                                >
                                    <img src={require('../../Assest/cards/MeridaConsultancyLogo.avif')} alt="Merida HR Logo" className="w-full h-auto object-contain" />
                                </motion.div>
                                <div className="absolute -inset-10 border border-slate-100 rounded-full animate-[spin_30s_linear_infinite]" />
                                <div className="absolute -inset-20 border border-dashed border-slate-200 rounded-full animate-[spin_60s_linear_infinite] reverse opacity-50" />
                            </div>

                            {/* Orbiting Requirement Nodes */}
                            {requirements.map((req, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, scale: 0 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: i * 0.1, type: 'spring' }}
                                    animate={{ y: [0, -12, 0], x: [0, 8, 0] }}
                                    transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut" }}
                                    className="absolute group"
                                    style={{
                                        // Orbit radius set to 34 to prevent overlap with the central logo
                                        top: `${50 + 34 * Math.sin(i * (2 * Math.PI / 7))}%`,
                                        left: `${50 + 34 * Math.cos(i * (2 * Math.PI / 7))}%`,
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                >
                                    <motion.div
                                        whileHover={{ scale: 1.1 }}
                                        className="relative p-3 md:p-4 rounded-2xl flex items-center gap-3 overflow-hidden border border-white/20 shadow-2xl"
                                        style={{ background: `linear-gradient(135deg, ${req.from}, ${req.to})` }}
                                    >
                                        <div className="absolute inset-0 bg-white/10 backdrop-blur-md opacity-40" />
                                        <div className="relative z-10 w-7 h-7 md:w-8 md:h-8 rounded-lg bg-white/20 flex items-center justify-center text-white">
                                            {React.cloneElement(req.icon, { size: 14 })}
                                        </div>
                                        <span className="relative z-10 text-[9px] md:text-[10px] font-black uppercase tracking-wider text-white whitespace-nowrap pr-2">
                                            {req.title}
                                        </span>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhoWeAre;